// ===================================================================
// KAIXO - AdminPanel principal
// ===================================================================
// Reemplaza al antiguo AdminSupportPanel. Contiene todas las pestañas
// del panel administrativo con RBAC + locks por área:
//
//   📊 Resumen           — todos los admins
//   📈 Estadísticas      — admin_general / admin_maintenance
//   💬 Soporte           — admin_general / admin_support
//   ✓ Verificaciones    — admin_general / admin_verification
//   👤 Usuarios          — admin_general
//   💰 Cobros            — admin_general / admin_accounting
//   💸 Gastos            — admin_general / admin_accounting
//   🧾 Facturas          — admin_general / admin_accounting
//   🚨 Alarmas           — admin_general / admin_maintenance
//   ⚙️  Configuración    — admin_general
//
// Mobile guard: si la pantalla es < 900px de ancho muestra mensaje
// "este panel solo está disponible en PC/Mac" y bloquea todo.
//
// Cada pestaña que NO sea Resumen/Configuración Y el user NO sea
// admin_general muestra el modal de unlock pidiendo contraseña de área.
// ===================================================================

import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { Icons } from '../Icons/Icons';
import { adminMe, clearAllAreaTokens } from '../../services/adminApi';
import useAreaAccess from './useAreaAccess';
import AreaUnlockModal from './AreaUnlockModal';
import AdminSupportSection from './sections/AdminSupportSection';
import AdminOverviewSection from './sections/AdminOverviewSection';
import AdminVerificationsSection from './sections/AdminVerificationsSection';
import AdminConfigSection from './sections/AdminConfigSection';
import AdminAuditSection from './sections/AdminAuditSection';
import AdminStatsSection from './sections/AdminStatsSection';
import AdminUsersSection from './sections/AdminUsersSection';
import AdminAlarmsSection from './sections/AdminAlarmsSection';
import AdminRevenueSection from './sections/AdminRevenueSection';
import AdminExpensesSection from './sections/AdminExpensesSection';
import AdminInvoicesSection from './sections/AdminInvoicesSection';
import AdminInvestorSection from './sections/AdminInvestorSection';
// AdminPlatformsSection ya no se usa como tab raíz; vive como
// sub-pestaña dentro de AdminExpensesSection (Gastos).
import './AdminPanel.css';

const TABS = [
    { id: 'overview',     icon: '📊', label: 'Resumen',         requiresArea: null,           anyRole: ['admin_general', 'admin_support', 'admin_verification', 'admin_accounting', 'admin_maintenance'] },
    { id: 'stats',        icon: '📈', label: 'Estadísticas',    requiresArea: 'maintenance',  anyRole: ['admin_general', 'admin_maintenance'] },
    { id: 'support',      icon: '💬', label: 'Soporte',         requiresArea: 'support',      anyRole: ['admin_general', 'admin_support'] },
    { id: 'verification', icon: '✓',  label: 'Verificaciones',  requiresArea: 'verification', anyRole: ['admin_general', 'admin_verification'] },
    { id: 'users',        icon: '👤', label: 'Usuarios',        requiresArea: null,           anyRole: ['admin_general'] },
    { id: 'revenue',      icon: '💰', label: 'Cobros',          requiresArea: 'accounting',   anyRole: ['admin_general', 'admin_accounting'] },
    { id: 'expenses',     icon: '💸', label: 'Gastos',          requiresArea: 'accounting',   anyRole: ['admin_general', 'admin_accounting'] },
    { id: 'invoices',     icon: '🧾', label: 'Facturas',        requiresArea: 'accounting',   anyRole: ['admin_general', 'admin_accounting'] },
    { id: 'alarms',       icon: '🚨', label: 'Alarmas',         requiresArea: 'maintenance',  anyRole: ['admin_general', 'admin_maintenance'] },
    { id: 'audit',        icon: '📜', label: 'Auditoría',       requiresArea: null,           anyRole: ['admin_general'] },
    { id: 'investor',     icon: '📊', label: 'Parámetros',      requiresArea: null,           anyRole: ['admin_general'] },
    { id: 'config',       icon: '⚙️', label: 'Configuración',   requiresArea: null,           anyRole: ['admin_general'] }
];

function isMobileViewport() {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 900 || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
}

function AdminPanel({ currentUser, onClose }) {
    const [me, setMe] = useState(null);
    const [loadingMe, setLoadingMe] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [pendingUnlockTab, setPendingUnlockTab] = useState(null);
    const [isMobile, setIsMobile] = useState(isMobileViewport());

    // Cargar info del admin actual
    useEffect(() => {
        let cancelled = false;
        adminMe()
            .then(({ data }) => { if (!cancelled) setMe(data); })
            .catch(() => { if (!cancelled) setMe(null); })
            .finally(() => { if (!cancelled) setLoadingMe(false); });
        return () => { cancelled = true; };
    }, []);

    // Detectar resize a móvil
    useEffect(() => {
        const onResize = () => setIsMobile(isMobileViewport());
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Al desmontar, limpiar tokens de área de la sesión por seguridad
    useEffect(() => {
        return () => clearAllAreaTokens();
    }, []);

    const visibleTabs = useMemo(() => {
        if (!me) return [];
        const isGeneral = me.isGeneral;
        const userRoles = new Set(me.roles || []);
        return TABS.filter(t => {
            if (isGeneral) return true;
            return t.anyRole.some(r => userRoles.has(r));
        });
    }, [me]);

    const currentTab = TABS.find(t => t.id === activeTab) || TABS[0];

    const onTabClick = (tabId) => {
        const tab = TABS.find(t => t.id === tabId);
        if (!tab) return;
        // Si requiere área y no soy general, comprobar si hay token
        if (tab.requiresArea && !me?.isGeneral) {
            const tok = sessionStorage.getItem('kaixo_area_token_' + tab.requiresArea);
            if (!tok) {
                setPendingUnlockTab(tabId);
                return;
            }
        }
        setActiveTab(tabId);
    };

    const renderSection = () => {
        if (loadingMe) return <div className="ap-loading">Cargando…</div>;
        if (!me?.isAdmin) return <div className="ap-error">No tienes permisos de administrador.</div>;

        switch (currentTab.id) {
            case 'overview':
                return <AdminOverviewSection me={me} currentUser={currentUser} />;
            case 'support':
                return <AdminSupportSection currentUser={currentUser} />;
            case 'verification':
                return <AdminVerificationsSection />;
            case 'stats':
                return <AdminStatsSection />;
            case 'users':
                return <AdminUsersSection />;
            case 'revenue':
                return <AdminRevenueSection />;
            case 'expenses':
                return <AdminExpensesSection />;
            case 'invoices':
                return <AdminInvoicesSection />;
            case 'alarms':
                return <AdminAlarmsSection />;
            case 'audit':
                return <AdminAuditSection />;
            case 'investor':
                if (!me.isGeneral) return <div className="ap-error">Solo admin_general.</div>;
                return <AdminInvestorSection />;
            case 'config':
                return <AdminConfigSection />;
            default:
                return null;
        }
    };

    // ─── MOBILE GUARD ───
    // En móvil bloqueamos la mayoría de las pestañas (tablas anchas y forms
    // complejos no se ven bien) PERO permitimos Resumen e Inversores —
    // diseñadas para móvil y útiles en negociaciones / reuniones rápidas.
    const MOBILE_ALLOWED_TABS = new Set(['overview', 'investor']);
    if (isMobile) {
        // Si el tab activo no está permitido en móvil, lo redirigimos a Resumen.
        if (!MOBILE_ALLOWED_TABS.has(activeTab)) {
            return ReactDOM.createPortal(
                <div className="ap-overlay" onClick={onClose}>
                    <div className="ap-mobile-block" onClick={e => e.stopPropagation()}>
                        <div className="ap-mobile-icon">🖥️</div>
                        <h2>Panel completo solo en escritorio</h2>
                        <p>Las secciones de gestión (usuarios, cobros, gastos, etc.) están pensadas para teclado y pantalla amplia. Pero <strong>Resumen y Parámetros</strong> sí están disponibles aquí en móvil.</p>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 18, flexWrap: 'wrap' }}>
                            <button
                                className="ap-mobile-close"
                                onClick={() => setActiveTab('overview')}
                                style={{ background: '#ff6b35' }}
                            >
                                Ver Resumen
                            </button>
                            {me?.isGeneral && (
                                <button
                                    className="ap-mobile-close"
                                    onClick={() => setActiveTab('investor')}
                                    style={{ background: '#0096c7' }}
                                >
                                    Ver Parámetros
                                </button>
                            )}
                            <button className="ap-mobile-close" onClick={onClose} style={{ background: '#888' }}>Cerrar</button>
                        </div>
                    </div>
                </div>,
                document.body
            );
        }
        // Modo móvil reducido: header + sección permitida sin sidebar
        return ReactDOM.createPortal(
            <div className="ap-overlay" onClick={onClose}>
                <div className="ap-modal ap-modal-mobile" onClick={e => e.stopPropagation()}>
                    <div className="ap-mobile-header">
                        <div className="ap-brand">KAIXO Admin</div>
                        <div className="ap-mobile-tab-switch">
                            <button
                                className={activeTab === 'overview' ? 'is-active' : ''}
                                onClick={() => setActiveTab('overview')}
                            >📊 Resumen</button>
                            {me?.isGeneral && (
                                <button
                                    className={activeTab === 'investor' ? 'is-active' : ''}
                                    onClick={() => setActiveTab('investor')}
                                >📈 Parámetros</button>
                            )}
                        </div>
                        <button className="ap-mobile-close-x" onClick={onClose}>✕</button>
                    </div>
                    <div className="ap-mobile-content">
                        {renderSection()}
                    </div>
                </div>
            </div>,
            document.body
        );
    }

    return ReactDOM.createPortal(
        <div className="ap-overlay" onClick={onClose}>
            <div className="ap-modal" onClick={e => e.stopPropagation()}>
                <div className="ap-sidebar">
                    <div className="ap-sidebar-header">
                        <div className="ap-brand">KAIXO Admin</div>
                        {me?.isGeneral && <div className="ap-role-badge">General</div>}
                    </div>
                    <nav className="ap-tabs">
                        {visibleTabs.map(tab => (
                            <button
                                key={tab.id}
                                className={`ap-tab ${activeTab === tab.id ? 'is-active' : ''}`}
                                onClick={() => onTabClick(tab.id)}
                            >
                                <span className="ap-tab-icon">{tab.icon}</span>
                                <span className="ap-tab-label">{tab.label}</span>
                                {tab.requiresArea && !me?.isGeneral && (
                                    <span className="ap-tab-lock" title="Requiere contraseña de área">🔒</span>
                                )}
                            </button>
                        ))}
                    </nav>
                    <div className="ap-sidebar-footer">
                        <button className="ap-close-btn" onClick={onClose}>
                            <Icons.Close size={16} /> Cerrar panel
                        </button>
                    </div>
                </div>
                <div className="ap-content">
                    <div className="ap-content-header">
                        <h1>{currentTab.icon} {currentTab.label}</h1>
                    </div>
                    <div className="ap-content-body">
                        {renderSection()}
                    </div>
                </div>
            </div>

            {pendingUnlockTab && (
                <UnlockGate
                    tab={TABS.find(t => t.id === pendingUnlockTab)}
                    onUnlocked={() => {
                        setActiveTab(pendingUnlockTab);
                        setPendingUnlockTab(null);
                    }}
                    onCancel={() => setPendingUnlockTab(null)}
                />
            )}
        </div>,
        document.body
    );
}

// Pequeño envoltorio que monta el modal de unlock para una pestaña
// concreta. Se desmonta cuando termina (éxito o cancelación).
function UnlockGate({ tab, onUnlocked, onCancel }) {
    const { unlock, loading, error, attemptsLeft, lockedUntil } = useAreaAccess(tab.requiresArea);
    const handleUnlock = async (pwd) => {
        const ok = await unlock(pwd);
        if (ok) onUnlocked();
    };
    return (
        <AreaUnlockModal
            area={tab.requiresArea}
            onUnlock={handleUnlock}
            onCancel={onCancel}
            loading={loading}
            error={error}
            attemptsLeft={attemptsLeft}
            lockedUntil={lockedUntil}
        />
    );
}

export default AdminPanel;
